//----------------------------------------------------------------------------------------------------------------------
// Toggle Binding
//----------------------------------------------------------------------------------------------------------------------

import type { GameEventBus } from '../eventBus.ts';

// Interfaces
import type { Action } from '../../interfaces/action.ts';
import type { Binding, ToggleBindingDefinition } from '../../interfaces/binding.ts';
import type { DeviceType, DeviceValueReader, InputState } from '../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Options for configuring a toggle binding
 */
export interface ToggleBindingOptions
{
    /**
     * If true, toggle on falling edge (true -> false) instead of rising edge (false -> true)
     */
    invert ?: boolean;

    /**
     * Initial toggle state (defaults to false - off)
     */
    initialState ?: boolean;

    /**
     * Threshold for analog inputs (0.0 to 1.0)
     * When input value is >= threshold, it's considered "active" (true)
     * When input value is < threshold, it's considered "inactive" (false)
     * Defaults to 0.5 for analog inputs
     */
    threshold ?: number;

    /**
     * Value to emit when the toggle is in the "on" state for digital actions
     * If undefined, will emit boolean true
     * Ignored for analog actions
     */
    onValue ?: boolean | number;

    /**
     * Value to emit when the toggle is in the "off" state for digital actions
     * If undefined, will emit boolean false
     * Ignored for analog actions
     */
    offValue ?: boolean | number;

    /**
     * Optional context name this binding belongs to
     */
    context ?: string;
}

/**
 * Implementation of a toggle binding, which maintains and toggles state between true/false
 * when a button/key is pressed or released, remaining in that state until toggled again.
 * Can handle both digital and analog inputs with configurable threshold.
 * Can output either digital or analog values based on the action type.
 */
export class ToggleBinding implements Binding
{
    public readonly type = 'toggle' as const;
    public readonly action : Action;
    public readonly context ?: string;
    public readonly deviceID : string;
    public readonly deviceType : DeviceType;
    public readonly reader : DeviceValueReader;

    // Toggle-specific options
    private readonly _invert : boolean;
    private readonly _threshold : number;
    private readonly _initialState : boolean;
    private readonly _onValue : boolean | number;
    private readonly _offValue : boolean | number;

    // State tracking
    private _lastDigitalState = false;
    private _toggleState : boolean;

    //------------------------------------------------------------------------------------------------------------------
    // Public Getters
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Get the current toggle state (true = on, false = off)
     */
    public get state() : boolean
    {
        return this._toggleState;
    }

    /**
     * Set the toggle state directly (useful for programmatic control)
     */
    public set state(value : boolean)
    {
        this._toggleState = value;
    }

    /**
     * Get the value emitted when toggle is in the "on" state
     */
    public get onValue() : boolean | number
    {
        return this._onValue;
    }

    /**
     * Get the value emitted when toggle is in the "off" state
     */
    public get offValue() : boolean | number
    {
        return this._offValue;
    }

    /**
     * Get the current value based on toggle state.
     * For analog actions, returns min/max value. For digital, returns on/off value.
     */
    public get value() : boolean | number
    {
        if(this.action.type === 'analog')
        {
            return this._toggleState
                ? (this.action.maxValue ?? 1)
                : (this.action.minValue ?? 0);
        }
        else
        {
            return this._toggleState ? this._onValue : this._offValue;
        }
    }

    /**
     * Get the current options for this toggle binding.
     */
    public get options() : ToggleBindingOptions
    {
        return {
            invert: this._invert,
            threshold: this._threshold,
            initialState: this._initialState,
            onValue: this._onValue,
            offValue: this._offValue,
        };
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new toggle binding
     *
     * @param action
     * @param deviceID
     * @param deviceType
     * @param reader
     * @param options
     */
    constructor(
        action : Action,
        deviceID : string,
        deviceType : DeviceType,
        reader : DeviceValueReader,
        options : ToggleBindingOptions = {}
    )
    {
        this.action = action;
        this.deviceID = deviceID;
        this.deviceType = deviceType;
        this.reader = reader;
        this.context = options.context;

        // Set options with defaults
        this._invert = options.invert ?? false;
        this._threshold = options.threshold ?? 0.5;
        this._initialState = options.initialState ?? false;
        this._toggleState = this._initialState;

        // For digital actions, these control the output value
        // For analog actions, these are not used (we use action properties instead)
        this._onValue = options.onValue ?? true;
        this._offValue = options.offValue ?? false;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Process input state and emit an action event if the toggle flipped.
     *
     * @param state
     * @param eventBus
     */
    public process(state : InputState, eventBus : GameEventBus) : void
    {
        const rawValue = this.reader.getValue(state) ?? false;
        const digitalState = typeof rawValue === 'boolean'
            ? rawValue
            : rawValue >= this._threshold;

        const shouldToggle = this._invert
            ? !digitalState && this._lastDigitalState
            : digitalState && !this._lastDigitalState;

        this._lastDigitalState = digitalState;

        if(!shouldToggle) { return; }

        this._toggleState = !this._toggleState;

        eventBus.publish({
            type: `action:${ this.action.name }`,
            payload: {
                value: this.value,
                deviceId: this.deviceID,
                context: this.context,
            },
        });
    }

    /**
     * Reset edge-detection state for a context activation. When a state is provided, primes from the current physical
     * input so that already-held inputs don't produce phantom edges in the newly activated context.
     */
    public resetEdgeState(state ?: InputState) : void
    {
        if(state)
        {
            const rawValue = this.reader.getValue(state) ?? false;
            this._lastDigitalState = typeof rawValue === 'boolean'
                ? rawValue
                : rawValue >= this._threshold;
        }
        else
        {
            this._lastDigitalState = false;
        }
    }

    /**
     * Reset toggle to its initial state
     */
    public reset() : void
    {
        this._toggleState = this._initialState;
    }

    /**
     * Returns a JSON-serializable representation of this toggle binding.
     */
    public toJSON() : ToggleBindingDefinition
    {
        return {
            type: this.type,
            action: this.action.name,
            input: {
                deviceID: this.deviceID,
                ...this.reader.toJSON(),
            },
            state: this._toggleState,
            context: this.context,
            options: this.options,
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
